import React, { Suspense } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import AOS from 'aos';
import 'aos/dist/aos.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Product from './components/product/Product';
import scrollToElement from '../services/smooth_scroll';
import observeElement from '../services/intersection_observer';
import Navigation from './components/product/navigation/Navigation';
import lightenOrDarker from '../services/lighten_or_darken';

import IngredientsList from './components/ingredients_list/IngredientsList';
import Benefits from './components/benefits/Benefits';
import HowToUse from './components/how_to_use/HowToUse';
import ClinicalStudy from './components/clinical_study/ClinicalStudy';

import Accordion from '../ui_kit/accordion/Accordion';

const ReviewsSection = React.lazy(() => import('./components/reviews_section/ReviewsSection'));
const ProductPairs = React.lazy(() => import('./components/product_pairs/ProductPairs'));

document.addEventListener('DOMContentLoaded', () => {
  const productElementId = 'react-product';
  const productInfoElement = document.getElementById(productElementId);
  const productData = JSON.parse(document.querySelector(`script[data-dom-id="${productElementId}"]`).textContent);
  const {
    subscriptionDiscountPercentage, product, variant, images,
  } = productData;
  hydrateRoot(
    productInfoElement,
    <Provider store={window.store}>
      <Product
        product={product}
        variant={variant}
        images={images}
        subscriptionDiscountPercentage={parseFloat(subscriptionDiscountPercentage)}
      />
    </Provider>,
  );

  const navigationElement = document.getElementById('react-navigation');
  createRoot(
    navigationElement,
  ).render(
    <Navigation
      sections={[
        {
          name: 'Ingredients',
          url: 'ingredients',
        }, {
          name: 'Innovator',
          url: 'innovator',
        }, {
          name: 'Reviews',
          url: 'react-reviews',
        }, {
          name: 'FAQ',
          url: 'react-productfaq-accordion',
        },
      ]}
    />,
  );

  const ingredientsAccordionElementId = 'react-ingredients-accordion-component';
  const ingredientsAccordionElement = document.getElementById(ingredientsAccordionElementId);

  if (ingredientsAccordionElement) {
    const data = JSON.parse(document.querySelector(`script[data-dom-id="${ingredientsAccordionElementId}"]`).textContent);
    const {
      ingredientHeading, ingredientContent, detailedIngredientsHeading, detailedIngredients,
      opened, valueTags,
    } = data;

    hydrateRoot(
      ingredientsAccordionElement,
      <Accordion
        heading={<div dangerouslySetInnerHTML={{ __html: ingredientHeading }} />}
        content={(
          <IngredientsList
            text={ingredientContent}
            detailedIngredientsHeading={detailedIngredientsHeading}
            detailedIngredients={detailedIngredients}
            valueTags={valueTags}
          />
      )}
        background="transparent"
        headingClassName="accordion-head"
        isOpened={opened}
      />,
    );
  }

  const featuresAccordionElementId = 'react-features-accordion-component';
  const featuresAccordionElement = document.getElementById(featuresAccordionElementId);
  if (featuresAccordionElement) {
    const data = JSON.parse(document.querySelector(`script[data-dom-id="${featuresAccordionElementId}"]`).textContent);
    const { featuresHeading, featuresContent, opened } = data;

    hydrateRoot(
      featuresAccordionElement,
      <Accordion
        heading={featuresHeading}
        content={(
          <Benefits
            featuresContent={featuresContent}
            previewText={product.previewText}
            pageColor={product.pageColor}
          />
        )}
        background="transparent"
        headingClassName="accordion-head"
        isOpened={opened}
      />,
    );
  }

  const clinicalStudyAccordionElementId = 'react-clinicalstudy-accordion-component';
  const clinicalStudyAccordionElement = document.getElementById(clinicalStudyAccordionElementId);
  if (clinicalStudyAccordionElement) {
    const data = JSON.parse(document.querySelector(`script[data-dom-id="${clinicalStudyAccordionElementId}"]`).textContent);
    const { clinicalStudyHeading, opened, slug } = data;

    hydrateRoot(
      clinicalStudyAccordionElement,
      <Provider store={window.store}>
        <Accordion
          heading={clinicalStudyHeading}
          content={(
            <ClinicalStudy slug={slug} />
          )}
          background="transparent"
          headingClassName="accordion-head"
          isOpened={opened}
        />
      </Provider>,
    );
  }

  const howToUseElementId = 'react-howtouse-accordion-component';
  const howToUseElement = document.getElementById(howToUseElementId);
  if (howToUseElement) {
    const data = JSON.parse(document.querySelector(`script[data-dom-id="${howToUseElementId}"]`).textContent);
    const { howToHeading, howToContent, opened } = data;

    hydrateRoot(
      howToUseElement,
      <Accordion
        heading={<div dangerouslySetInnerHTML={{ __html: howToHeading }} />}
        content={<HowToUse text={howToContent} />}
        background="transparent"
        headingClassName="accordion-head"
        isOpened={opened}
      />,
    );
  }

  const productFaqElement = document.getElementById('react-productfaq-accordion');
  if (productFaqElement) {
    const { faqHeading, faqContent } = productFaqElement.dataset;

    observeElement(productFaqElement, () => {
      createRoot(
        productFaqElement,
      ).render(
        <Suspense fallback="">
          <Accordion
            heading={<div dangerouslySetInnerHTML={{ __html: faqHeading }} />}
            content={<div dangerouslySetInnerHTML={{ __html: faqContent }} />}
            background="spring-wood"
            headingBackground="spring-wood"
            headingClassName="accordion-head"
            isOpened
          />
        </Suspense>,
      );
    }, '300px');
  }

  if (!product.waitlistSubscribable) {
    const AdditionalProduct = React.lazy(() => import('./components/additional_product/AdditionalProduct'));

    const secondButtonElement = document.getElementById('second-add-to-bag');

    observeElement(secondButtonElement, () => {
      createRoot(
        secondButtonElement,
      ).render(
        <Suspense fallback="">
          <Provider store={window.store}>
            <AdditionalProduct
              product={product}
              variant={variant}
              pageColor={product.pageColor}
              key="second"
            />
          </Provider>
        </Suspense>,
      );
    }, '300px');
  }

  const videoElement = document.getElementById('react-video');
  if (videoElement) {
    const { videoUrl } = videoElement.dataset;

    const Video = React.lazy(() => import('./components/video/Video'));

    observeElement(videoElement, () => {
      createRoot(
        videoElement,
      ).render(
        <Suspense fallback="">
          <Video videoUrl={videoUrl} />
        </Suspense>,
      );
    }, '300px');
  }

  const questionsAndAnswersSection = document.getElementById('react-qa');
  if (questionsAndAnswersSection) {
    const { firstName } = questionsAndAnswersSection.dataset;
    let { questionsAndAnswers } = questionsAndAnswersSection.dataset;
    questionsAndAnswers = JSON.parse(questionsAndAnswers);

    const QuestionsAndAnswers = React.lazy(() => import('../user/components/user/innovations/questions_and_answers/QuestionsAndAnswers'));

    observeElement(questionsAndAnswersSection, () => {
      createRoot(
        questionsAndAnswersSection,
      ).render(
        <Suspense fallback="">
          <Provider store={window.store}>
            <QuestionsAndAnswers
              firstName={firstName}
              questionsAndAnswers={questionsAndAnswers}
              pageColor={product.pageColor}
            />
          </Provider>
        </Suspense>,
      );
    }, '300px');
  }

  const searchParams = new URLSearchParams(window.location.search);
  const submitReviewMode = searchParams.get('open_review_form') === 'true';

  const reviewSection = document.getElementById('react-reviews');

  createRoot(
    reviewSection,
  ).render(
    <Suspense fallback="">
      <Provider store={window.store}>
        <ReviewsSection
          sourceId={reviewSection.getAttribute('product-id')}
          customColor={product.pageColor}
          submitReviewMode={submitReviewMode}
          customTransparentButtonStyles={(
            `\
              .custom-transparent-button{\
                border: 1px solid ${product.pageColor};\
              }\
              .custom-transparent-button:hover {\
                background-color: ${lightenOrDarker(product.pageColor, -7)}\
              }\
              .loading {\
                background-color: ${lightenOrDarker(product.pageColor, -7)} !important\
              }\
            `
          )}
          customFullButtonStyle={(
            `\
              .custom-button{\
                background-color: ${product.pageColor};\
                border: 1px solid ${product.pageColor};\
              }\
              .custom-button:hover {\
                background-color: ${lightenOrDarker(product.pageColor, -7)};\
                border: 1px solid ${lightenOrDarker(product.pageColor, -7)};\
              }\
              .loading {\
                background-color: ${lightenOrDarker(product.pageColor, -7)} !important\
              }\
            `
          )}
        />
      </Provider>
    </Suspense>,
  );

  const productPairsSection = document.getElementById('product-pairs');
  observeElement(productPairsSection, () => {
    createRoot(
      productPairsSection,
    ).render(
      <Suspense fallback="">
        <Provider store={window.store}>
          <ProductPairs productId={product.id} />
        </Provider>
      </Suspense>,
    );
  }, '300px');

  const scrollToElementId = searchParams.get('scroll_to');
  if (scrollToElementId) {
    scrollToElement(scrollToElementId);
  }

  AOS.init();
});
